import { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList from '@App/components/PanelList/PanelList';
import { useMunicipalityDetails } from '@services/query.service';
import { formatEnergyPerYear } from '@utils/energy.utils';
import { getFeaturesBounds, getGeometryAreaKm2 } from '@utils/geometry.utils';

import { layers as municipalityLayers } from './municipality.layers';
import {
  getProvinceName,
} from './municipality.utils';

import './municipality.css';

const numberFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatNumber(value) {
  return value === undefined || value === null
    ? '-'
    : numberFormatter.format(value);
}

function formatArea(value) {
  return value === undefined || value === null
    ? '-'
    : `${numberFormatter.format(value)} km²`;
}

function Municipality() {
  const { map } = useOutletContext();
  const { municipalityId } = useParams();
  const { data: municipality } = useMunicipalityDetails(municipalityId);
  const { Naam: name } = municipality || {};
  const areaKm2 = getGeometryAreaKm2(municipality?.geometry);
  const municipalityDetails = [
    ['Provincie', getProvinceName(municipality?.ID_gemeente ?? municipalityId)],
    ['Inwonersaantal', formatNumber(municipality?.Inwonersaantal)],
    ['Oppervlakte', formatArea(areaKm2)],
    ['Warmtevraag', formatEnergyPerYear(municipality?.warmtevraag)],
    [
      'Warmte ontvangen',
      formatEnergyPerYear(municipality?.warmte_ontvangen_combosleutel_bs6_vs1),
    ],
    [
      'AQ-potentieel',
      formatNumber(municipality?.AQ_score_combosleutel_bs6_vs1),
    ],
  ];

  useEffect(() => {
    if (!map || !municipalityId || !municipality) return undefined;

    let hasFittedBounds = false;

    const syncMunicipality = () => {
      if (!map.getSource('gemeente')) {
        return;
      }

      if (!map.getLayer(municipalityLayers.municipalityMask.id)) {
        map.addLayer(municipalityLayers.municipalityMask);
      }

      if (!map.getLayer(municipalityLayers.selectedMunicipality.id)) {
        map.addLayer(municipalityLayers.selectedMunicipality);
      }

      map.setFilter(municipalityLayers.municipalityMask.id, [
        '!=',
        ['get', 'ID_gemeente'],
        municipalityId,
      ]);
      map.setFilter(municipalityLayers.selectedMunicipality.id, [
        '==',
        ['get', 'ID_gemeente'],
        municipalityId,
      ]);

      const gemeenteBounds = getFeaturesBounds([municipality]);

      if (gemeenteBounds && !hasFittedBounds) {
        hasFittedBounds = true;
        map.fitBounds(gemeenteBounds, {
          padding: 48,
          maxZoom: 16,
          duration: 1000,
        });
        map.off('idle', syncMunicipality);
      }
    };

    syncMunicipality();
    map.on('idle', syncMunicipality);

    return () => {
      map.off('idle', syncMunicipality);
      if (map.getLayer(municipalityLayers.municipalityMask.id)) {
        map.removeLayer(municipalityLayers.municipalityMask.id);
      }
      if (map.getLayer(municipalityLayers.selectedMunicipality.id)) {
        map.removeLayer(municipalityLayers.selectedMunicipality.id);
      }
    };
  }, [map, municipality, municipalityId]);

  return (
    <div className="municipality">
      <PanelHeader
        map={map}
        municipalityId={municipalityId}
        municipalityName={name}
      />
      <PanelList details={municipalityDetails} />
    </div>
  );
}

export default Municipality;
